import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Coffee, TrendingUp } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

interface BusinessOption {
  to: string
  name: string
  description: string
  icon: typeof Coffee
}

const BUSINESSES: BusinessOption[] = [
  {
    to: '/admin',
    name: 'Café Saldaña',
    description: 'Distribución de café: ventas, producción, logística y finanzas.',
    icon: Coffee,
  },
  {
    to: '/fx',
    name: 'FX Admin',
    description: 'Compra y venta de productos: inventario, clientes y finanzas.',
    icon: TrendingUp,
  },
]

/** Post-login landing for the admin account, which manages more than one
 * business from this same portal (see infra/db/027_add_distribution_fx.sql
 * for why FX Admin lives in its own schema rather than inside `distribution`
 * or behind a tenant_id). Sellers/collaborators never see this -- they only
 * ever work in the coffee business and getLandingRoute() sends them straight
 * there. Reachable again any time via the sidebar logo/"Cambiar de negocio",
 * since IndexRedirect always re-resolves getLandingRoute(). */
export default function ChooseBusinessPage() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (loading) return
    if (!user) {
      navigate('/login')
      return
    }
    if (user.role !== 'admin') {
      navigate('/dashboard')
    }
  }, [user, loading, navigate])

  if (loading || !user || user.role !== 'admin') {
    return null
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-cream-100 px-4">
      <div className="w-full max-w-xl">
        <h1 className="text-center text-2xl font-bold text-espresso-800 mb-2">
          ¿Qué negocio quieres gestionar?
        </h1>
        <p className="text-center text-sm text-espresso-500 mb-8">
          Puedes cambiar de negocio en cualquier momento desde el menú lateral.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {BUSINESSES.map((business) => {
            const Icon = business.icon
            return (
              <button
                key={business.to}
                type="button"
                onClick={() => navigate(business.to)}
                className="flex flex-col items-start gap-3 rounded-lg border border-espresso-200 bg-cream-50 p-6 text-left shadow-sm transition-colors hover:border-terracotta-400 hover:bg-terracotta-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-terracotta-100 text-terracotta-700">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <span className="text-lg font-semibold text-espresso-800">{business.name}</span>
                <span className="text-sm text-espresso-500">{business.description}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
